import Header from '@lilbit/shared/src/components/Header';
import Footer from '@lilbit/shared/src/components/Footer';
import {
  defaultCategoryLocales,
  defaultSlugLocales,
  getValidLocale,
  locales,
  localize,
} from '@lilbit/shared';
import { GetStaticProps, GetStaticPaths } from 'next';
import {
  getFooter,
  getLildogProductPrices,
  getProductForFrontPage,
  getSiteConfig,
} from '../../../lib/api';
import { Footerv2Props } from '@lilbit/shared';
import { urlFor } from '../../../lib/sanity';
import SimpleBlockContent from '../../../components/SimpleBlockContent';
import { useLocale } from '@lilbit/shared';

interface Props {
  mainProduct: any;
  config: any;
  mainProductPrices: any;
  locale: any;
  footer: Footerv2Props;
}

const Privacy = (props: Props) => {
  const ulrPrefix = useLocale().urlPrefix;
  const { mainProduct, config, footer } = props;
  return (
    <div>
      <Header
        config={config}
        urlFor={urlFor}
        siteName={'lildog'}
        mainProduct={mainProduct}
      />
      <div
        className="h-fit w-screen flex flex-col text-left lg:p-12 p-4 md:p-4 bg
       font-default text-base"
      >
        {ulrPrefix == 'no' ? (
          <div className="space-y-2 flex flex-col">
            <div className="flex flex-col w-full items-center text-center space-y-4 pb-6 md:pb-10 lg:pb-12">
              <h1 className="font-p22 text-3xl md:text-4xl mb-6">
                Personvernerklæring
              </h1>
              <p className='mb-6'>
                Ditt personvern er vår høyeste prioritet. For oss er det
                avgjørende å være åpne og ærlige om dataene vi samler inn fra
                deg. Enda viktigere er det for oss å håndtere og beskytte disse
                dataene med største mulig aktsomhet, slik at du, som en del av
                Lildog familien, føler deg trygg og rettferdig behandlet.
              </p>
            </div>
            <h2 className="font-bold">Databeskyttelse og samsvar</h2>
            <p>
              Behandlingen av dine data er beskyttet av EU{`'`}s
              databeskyttelseslover, inkludert personvernforordningen (GDPR).
            </p>

            <h2 className="font-bold">Datainnsamling og behandling</h2>
            <p>
              Når du bruker våre enheter og tjenester, vil personopplysninger
              bli behandlet for både personen som setter opp tjenesten og
              brukeren av enheten. Nedenfor finner du informasjon om de
              personopplysningene som samles inn, hvordan de behandles, og dine
              rettigheter knyttet til behandlingen av disse dataene.
            </p>

            <h2 className="font-bold">Ansvarlig enhet</h2>
            <p>
              Selskapet som er ansvarlig for behandlingen av dine
              personopplysninger er Lildog AS, org. nr. 817 870 902. For
              spørsmål om behandlingen av personopplysninger, kan du kontakte
              oss på support@lildog.com eller på telefon +47 73 44 00 70. Når
              {`'`}vi{`'`}, {`'`}vår{`'`} eller {`'`}oss{`'`} brukes nedenfor,
              refererer det til Lildog AS.
            </p>

            <h2 className="font-bold">
              Formål og juridisk grunnlag for behandling
            </h2>
            <p>
              Formålet og det juridiske grunnlaget for behandling av alle
              personopplysninger er å levere tjenestene knyttet til våre enheter
              og applikasjoner, som beskrevet i GDPR artikkel 6 nr. 1 bokstav
              b).
            </p>

            <h2 className="font-bold">
              Kategorier av personopplysninger som samles inn og behandles
            </h2>
            <div className="w-full px-8 flex flex-col items-left">
              <h3 className="font-bold">
                1. Informasjon du gir når du kjøper eller registrerer et produkt
              </h3>

              <div className="w-full px-8 flex flex-col items-left">
                <ul className="list-with-dots">
                  <li>Telefonnummer</li>
                  <li>Enhetens IMEI-nummer</li>
                  <li>Postadresse</li>
                  <li>E-postadresse</li>
                </ul>
              </div>

              <h3 className="font-bold">
                2. Informasjon du gir når du bruker enheten
              </h3>

              <div className="w-full px-8 flex items-left">
                <ul className="list-with-dots">
                  <li>Navn</li>
                  <li>E-postadresse</li>
                  <li>Passord</li>
                  <li>
                    Kjæledyrets detaljer (navn, rase, kjønn, størrelse,
                    fødselsdato, profilbilde, trivia)
                  </li>
                </ul>
              </div>

              <h3 className="font-bold">
                3. Informasjon vi mottar når du bruker våre tjenester
              </h3>

              <div className="w-full px-8 flex flex-col  items-left">
                <ul className="list-with-dots">
                  <li>
                    <strong>Bruksdata:</strong> Informasjon om hvordan du
                    samhandler med våre tjenester.
                  </li>
                  <li>
                    <strong>Enhetsinformasjon:</strong> Detaljer om enheten du
                    bruker, inkludert modell, operativsystem,
                    maskinvareidentifikatorer, nettverksinformasjon og
                    sensordata.
                  </li>
                  <li>
                    <strong>Stedsdata:</strong> Presis og generell
                    stedsinformasjon samlet inn via GPS, Wi-Fi og andre
                    teknologier.
                  </li>
                  <li>
                    <strong>Logginformasjon:</strong> Nettlesertype,
                    tilgangstider, besøkte sider, IP-adresse og identifikatorer
                    knyttet til informasjonskapsler eller andre teknologier.
                  </li>
                  <li>
                    <strong>Sensordata:</strong> Informasjon samlet inn fra
                    forskjellige sensorer på enhetene, som kan inkludere, men
                    ikke er begrenset til, akselerometre, gyroskoper,
                    mikrofoner, høyttalere, temperatursensorer og andre
                    relevante sensorer. Hvilke spesifikke sensorer og data som
                    samles inn, avhenger av enhetsmodellen og dens kapasitet.
                  </li>
                </ul>
              </div>
            </div>
            <h2 className="font-bold">Formål med databehandling</h2>
            <div className="w-full px-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>
                  <strong>Tjenestelevering:</strong> Å levere og vedlikeholde
                  våre tjenester, og sikre at de fungerer korrekt på tvers av
                  forskjellige enheter og applikasjoner.
                </li>
                <li>
                  <strong>Forbedring av brukeropplevelse:</strong> Å forbedre
                  brukeropplevelsen ved å tilby personaliserte tjenester og
                  anbefalinger basert på innsamlede data.
                </li>
                <li>
                  <strong>Statistisk analyse:</strong> Å samle og analysere
                  generelle statistikker (f.eks. gjennomsnittlige
                  aktivitetsnivåer for visse hunderaser) for å gi sammenlignende
                  innsikt til brukere.
                </li>
                <li>
                  <strong>Sikkerhet og svindelforebygging:</strong> Å verifisere
                  identiteter og forhindre uautorisert eller svindelaktig
                  aktivitet.
                </li>
              </ul>
            </div>

            <h2 className="font-bold">Hvordan vi bruker informasjon</h2>
            <p>
              Informasjonen som samles inn brukes til å levere, utvikle,
              forbedre og sikre våre tjenester. Det inkluderer:
            </p>
            <div className="w-full px-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>Å sende informasjon og oppdateringer</li>
                <li>Å verifisere identitet og forhindre svindel</li>
                <li>Å forbedre brukeropplevelsen</li>
              </ul>
            </div>
            <h2 className="font-bold">Lagring og sletting av data</h2>
            <p>
              Personopplysninger behandles så lenge det er et grunnlag og formål
              for behandlingen. Du kan slette personopplysninger gjennom vår
              applikasjon eller nettside, eller ved å kontakte kundeservice.
              Sletting av nødvendige opplysninger kan føre til tap av tilgang
              til tjenesten.
            </p>
            <h2 className="font-bold">Kontroll over din informasjon</h2>
            <p>
              Du har verktøy for å få tilgang til, oppdatere og trekke tilbake
              samtykke for personopplysninger:
            </p>
            <div className="w-full px-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>
                  Tilgang og oppdatering av informasjon gjennom appen eller
                  nettsiden
                </li>
                <li>
                  Tilbaketrekking av samtykke for visse datainnsamlinger via
                  enhetsinnstillinger
                </li>
                <li>
                  Slette kontoen din gjennom appen eller ved å kontakte
                  kundeservice
                </li>
              </ul>
            </div>
            <h2 className="font-bold">Databehandlere</h2>
            <p>
              Vi bruker en kombinasjon av tredjepartstjenester og interne
              verktøy for å behandle data og levere våre tjenester. Dette
              inkluderer:
            </p>
            <div className="w-full px-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>
                  <strong>Tredjepartstjenester:</strong> Vi engasjerer ulike
                  tredjepartstjenesteleverandører for skybasert databehandling,
                  datalagring, analyse, kundestøtte og nettverkstjenester. Disse
                  inkluderer, men er ikke begrenset til, Amazon Web Services
                  (AWS), Google Cloud, Firebase, Zendesk, Stripe, Telia,
                  Telenor, Facebook, Google Analytics, Google Adwords og Gitlab.
                </li>
                <li>
                  <strong>Interne verktøy:</strong> Vi bruker også internt
                  administrerte verktøy og tjenester for databehandling,
                  overvåking og analyse. Disse verktøyene er vert på vår
                  infrastruktur og brukes for å sikre optimal ytelse og
                  sikkerhet for våre tjenester.
                </li>
              </ul>
            </div>
            <h2 className="font-bold">Juridisk samsvar og åpenhet</h2>
            <p>
              Lildog AS sikrer samsvar med GDPR ved å gi klar informasjon om
              datainnsamling og behandling. Selv om vi ikke har formelle
              databehandleravtaler med alle tredjeparts tjenesteleverandører,
              velger vi nøye leverandører som er kjent for sine sterke
              databeskyttelsespraksiser. På samme måte administreres våre
              interne verktøy med strenge databeskyttelsestiltak for å beskytte
              dine personopplysninger.
            </p>
            <h2 className="font-bold">Klager</h2>
            <p>
              Hvis du mener at dine data har blitt feilbehandlet, kan du
              kontakte oss eller Datatilsynet på{' '}
              <a href="https://www.datatilsynet.no">www.datatilsynet.no</a>.
            </p>
            <h2 className="font-bold">Endringer</h2>
            <p>
              Vi kan oppdatere denne personvernerklæringen etter behov. Den
              nyeste versjonen vil alltid være tilgjengelig på vår nettside.
            </p>
          </div>
        ) : (
          <div className="space-y-2 flex flex-col">
            <div className="flex flex-col w-full items-center text-center space-y-4 pb-6 md:pb-10 lg:pb-12">
              <h1 className="font-p22 text-3xl md:text-4xl mb-6">
                Privacy Statement
              </h1>
              <p className="mb-6">
                Your privacy is our top priority. For us, it is essential to be
                transparent and honest about the data we collect from you. Even
                more so, we find it necessary to handle and protect this data
                with the utmost care so you, as a part of the Lildog family,
                feel safe and fairly treated
              </p>
            </div>

            <p className="font-bold">Data Protection and Compliance</p>
            <p>
              The handling of your data is protected by EU data protection and
              privacy laws, including the General Data Protection Regulation
              (GDPR).
            </p>
            <p className="font-bold">Data Collection and Processing</p>
            <p>
              When using our devices and services, personal data will be
              processed for both the person who sets up the service and the user
              of the device. Below is information on the personal data
              collected, how it is processed, and your rights related to the
              processing of this data.
            </p>
            <p className="font-bold">Responsible Entity</p>
            <p>
              The company responsible for processing your personal data is
              Lildog AS, org. no. 817 870 902. For any questions about the
              processing of personal data, you can contact us at
              support@lildog.com or by telephone at +47 73 44 00 70. When {`'`}
              we{`'`}, {`'`}our{`'`}, or {`'`}us{`'`} is used below, it refers
              to Lildog AS.
            </p>
            <p className="font-bold">Purpose and Legal Basis for Processing</p>
            <p>
              The purpose and legal basis for processing all personal data is to
              provide the services related to our devices and applications, as
              outlined in GDPR Article 6 no. 1 point b).
            </p>
            <p className="font-bold">
              Categories of Personal Information Collected and Processed
            </p>
            <div className="w-full pl-8 flex flex-col items-left">
              <h2 className="font-bold">
                1. Information You Provide When Purchasing or Registering a
                Product
              </h2>
              <div className="w-full pl-8 flex flex-col items-left">
                <ul className="list-with-dots">
                  <li>Phone number</li>
                  <li>Device IMEI number</li>
                  <li>Postal address</li>
                  <li>Email address</li>
                </ul>
              </div>
              <h2 className="font-bold">
                2. Information You Provide When Using the Device
              </h2>

              <div className="w-full pl-8 flex flex-col items-left">
                <ul className="list-with-dots">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Password</li>
                  <li>
                    Pet details (name, breed, gender, size, date of birth,
                    profile picture, trivia)
                  </li>
                </ul>
              </div>
              <h2 className="font-bold">
                3. Information We Receive When You Use Our Services
              </h2>

              <div className="w-full pl-8 flex flex-col items-left">
                <ul className="list-with-dots">
                  <li>
                    <strong>Usage Data:</strong> Information about how you
                    interact with our services.
                  </li>
                  <li>
                    <strong>Device Information:</strong> Details about the
                    device you use, including model, operating system, hardware
                    identifiers, network information, and sensor data.
                  </li>
                  <li>
                    <strong>Location Data:</strong> Precise and general location
                    information collected via GPS, Wi-Fi, and other
                    technologies.
                  </li>
                  <li>
                    <strong>Log Information:</strong> Browser type, access
                    times, pages visited, IP address, and identifiers associated
                    with cookies or other technologies.
                  </li>
                  <li>
                    <strong>Sensor Data:</strong> Information collected from
                    various sensors on the devices, which may include but is not
                    limited to accelerometers, gyroscopes, microphones,
                    speakers, temperature sensors, and other relevant sensors.
                    The specific sensors and data collected depend on the device
                    model and its capabilities.
                  </li>
                </ul>
              </div>
            </div>
            <p className="font-bold">Purpose of Data Processing</p>
            <div className="w-full pl-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>
                  <strong>Service Provision:</strong> To provide and maintain
                  our services, ensuring they function correctly across
                  different devices and applications.
                </li>
                <li>
                  <strong>User Experience Enhancement:</strong> To enhance the
                  user experience by providing personalized services and
                  recommendations based on collected data.
                </li>
                <li>
                  <strong>Statistical Analysis:</strong> To gather and analyze
                  general statistics (e.g., average activity levels of certain
                  dog breeds) to provide comparative insights to users.
                </li>
                <li>
                  <strong>Security and Fraud Prevention:</strong> To verify
                  identities and prevent unauthorized or fraudulent activity.
                </li>
              </ul>
            </div>
            <h1 className="font-bold">How We Use Information</h1>
            <p>
              The information collected is used to provide, develop, improve,
              and secure our services. It includes:
            </p>
            <div className="w-full pl-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>Sending information and updates</li>
                <li>Verifying identity and preventing fraud</li>
                <li>Enhancing user experience</li>
              </ul>
            </div>

            <h1 className="font-bold">Data Retention and Deletion</h1>
            <p>
              Personal data is processed as long as there is a basis and purpose
              for its processing. You can delete personal information through
              our application or website, or by contacting customer service.
              Deleting necessary information may result in loss of service
              access.
            </p>

            <h1 className="font-bold">Control Over Your Information</h1>
            <p>
              You have tools to access, update, and revoke consent for personal
              information:
            </p>
            <div className="w-full pl-8 flex flex-col items-left">
              <ul className="list-with-dots">
                <li>
                  Access and update information through the app or website
                </li>
                <li>
                  Revoke consent for certain data collection via device settings
                </li>
                <li>
                  Delete your account through the app or by contacting customer
                  service
                </li>
              </ul>
            </div>
            <h1 className="font-bold">Data Processors</h1>
            <p>
              We use a combination of third-party services and internal tools to
              process data and provide our services. This includes:
            </p>
            <div className="w-full pl-8 flex flex-col text-left">
              <h2 className="font-bold">Third-Party Services:</h2>
              <p>
                We engage with various third-party service providers for cloud
                computing, data storage, analytics, customer support, and
                network services. These include, but are not limited to, Amazon
                Web Services (AWS), Google Cloud, Firebase, Zendesk, Stripe,
                Telia, Telenor, Facebook, Google Analytics, Google Adwords, and
                Gitlab.
              </p>
              <h2 className="font-bold">Internal Tools:</h2>
              <p>
                We also utilize internally managed tools and services for data
                processing, monitoring, and analysis. These tools are hosted
                within our infrastructure and are used to ensure the optimal
                performance and security of our services.
              </p>
            </div>
            <h1 className="font-bold">Legal Compliance and Transparency</h1>
            <p>
              Lildog AS ensures compliance with GDPR by providing clear
              information on data collection and processing. While we do not
              have formal data processing agreements with all third-party
              service providers, we carefully select providers known for their
              strong data protection practices. Similarly, our internal tools
              are managed with strict data protection measures to safeguard your
              personal information.
            </p>

            <h1 className="font-bold">Complaints</h1>
            <p>
              If you believe your data has been mishandled, you can contact us
              or the Data Inspectorate at{' '}
              <a href="https://www.datatilsynet.no">www.datatilsynet.no</a>.
            </p>

            <h1 className="font-bold">Changes</h1>
            <p>
              We may update this privacy statement as needed. The latest version
              will always be available on our website.
            </p>
          </div>
        )}
      </div>
      <Footer
        {...footer}
        urlFor={urlFor}
        SimpleBlockContent={SimpleBlockContent}
      />
    </div>
  );
};

export default Privacy;

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const locale = getValidLocale(params.lang as string);
  const sanityLanguageIdentifier = locale?.sanityLanguageIdentifier;
 
 
  
  let config = await getSiteConfig(sanityLanguageIdentifier);
  const footerData = await getFooter();
  const footer = localize(footerData, [sanityLanguageIdentifier]);

  let mainProductPrices = await getLildogProductPrices();
  let counter1 = 0;
  while (!mainProductPrices && counter1 < 10) {
    if (!mainProductPrices) {
      mainProductPrices = await getProductForFrontPage();
      counter1++;
    } else {
      break;
    }
  }

  if (config && defaultSlugLocales) {
    config.slugsForAllLocales = defaultSlugLocales;
  }
  if (config && defaultCategoryLocales)
    config.slugsForAllCategories = defaultCategoryLocales;
  const mainProduct = null;// await getWoocommerceProducts(true);
  return {
    props: {
      mainProduct,
      
      config,
      mainProductPrices,
      locale,
      footer,
    },
    revalidate: 60 * 60 * 24,
  };
};
export const getStaticPaths: GetStaticPaths = async () => {
  const paths = locales.map((l) => ({
    params: { lang: l.urlPrefix },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};
